import OncolensToast from '@/components/OncolensToast';
import { useOrganizations } from '@/features/organizations/api/organizations';
import { yupResolver } from '@hookform/resolvers/yup';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import { SelectItem } from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import Typeahead, {
  TypeaheadListItem,
} from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BsTrashFill } from 'react-icons/bs';
import * as yup from 'yup';
import { useAddOrgRole } from '../api/orgroles/addOrgRole';
import { useDeleteOrgRole } from '../api/orgroles/deleteOrgRole';
import { useGetOrgRoleListByUser } from '../api/orgroles/getOrgRoleList';
import { useGetRoles } from '../api/roles/getRoles';
import { OrgRoleForCreationDto } from '../types/index';

interface OrgRoleFormProps {
  userId: string | undefined;
}

const orgRoleSchema = yup.object().shape({
  userId: yup.string().required(),
  organizationId: yup.string().required(),
  role: yup.string().required(),
});

function OrgRolesForm({ userId }: OrgRoleFormProps) {
  const { data: orgRoles } = useGetOrgRoleListByUser(userId ?? '');
  const { data: rolesData } = useGetRoles();

  const roles = (rolesData || []).map((role) => ({
    value: role,
    label: role,
  }));

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    reset,
  } = useForm<OrgRoleForCreationDto>({
    resolver: yupResolver(orgRoleSchema),
  });

  const createNewOrgRole = useAddOrgRole();
  const onNewOrgRoleSubmit = (data: OrgRoleForCreationDto) => {
    createNewOrgRole.mutate(data);
  };
  const deleteNewOrgRole = useDeleteOrgRole();
  const onDeleteOrgRoleSubmit = (id: string) => {
    deleteNewOrgRole.mutate(id);
  };

  useEffect(() => {
    if (createNewOrgRole.isSuccess) {
      OncolensToast.success('Org Role added successfully');
      reset({
        userId,
      });
    }
    if (createNewOrgRole.isError) OncolensToast.error('Error occurred.');
  }, [createNewOrgRole.isError, createNewOrgRole.isSuccess, reset]);

  useEffect(() => {
    setValue('userId', userId ?? '');
  }, [setValue, userId]);

  const empty = [] as SelectItem[];
  const { data: organizations } = useOrganizations({ pageSize: 500, sortOrder: 'Name' });
  const organizationItems =
    (organizations?.data?.map((org) => ({
      value: org.id,
      label: org.name,
    })) as TypeaheadListItem[]) ?? empty;

  return (
    <div className="mt-6 space-y-6 divide-y divide-y-blue-gray-200">
      <div className="sm:flex sm:items-center sm:justify-between">
        <h2 className="text-xl font-medium text-blue-gray-900">Organization Roles</h2>
      </div>
      <div className="py-3">
        <form onSubmit={handleSubmit(onNewOrgRoleSubmit)} className="">
          <div className="flex max-w-3xl space-x-4">
            <div className="flex flex-1 space-x-4">
              <Controller
                name="organizationId"
                control={control}
                render={({ field }) => (
                  // TODO add an autofocus prop to ui-lib
                  <Typeahead
                    id="organizationId"
                    label="Organization"
                    required={true}
                    data={organizationItems}
                    className="w-1/2"
                    error={errors?.organizationId?.message}
                    selected={organizationItems.find((item) => item.value === field.value) || null}
                    onChange={(item) => {
                      field.onChange(item?.value);
                    }}
                    options={{
                      minCharactersForRemoteFetch: 2,
                      allowCustomInput: false,
                      useRegexMatchReplacement: false,
                    }}
                  />
                )}
              />
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Typeahead
                    id="role"
                    label="Role"
                    required={true}
                    data={roles}
                    className="w-1/2"
                    error={errors?.role?.message}
                    selected={roles.find((item) => item.value === field.value) || null}
                    onChange={(item) => {
                      field.onChange(item?.value);
                    }}
                    options={{
                      minCharactersForRemoteFetch: 2,
                      allowCustomInput: false,
                      useRegexMatchReplacement: false,
                    }}
                  />
                )}
              />
            </div>
            <div className="flex items-end justify-end">
              <ButtonWithLoading
                disabled={createNewOrgRole.isLoading}
                loading={createNewOrgRole.isLoading}
                buttonColor="blue"
                buttonStyle="secondary"
                type="submit">
                Add
              </ButtonWithLoading>
            </div>
          </div>
        </form>
      </div>
      <div className="overflow-auto">
        <div className="flow-root">
          <ul className="divide-y divide-gray-200">
            {orgRoles &&
              orgRoles?.map((orgRole) => (
                <li
                  key={`${orgRole.organizationId}${orgRole.role}`}
                  className="py-4 group hover:bg-gray-200/50">
                  <div className="flex items-center px-4 space-x-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{orgRole.role}</p>
                      <p className="text-sm text-gray-500 truncate">{orgRole.orgName}</p>
                    </div>
                    <div className="">
                      <button
                        onClick={() => onDeleteOrgRoleSubmit(orgRole.id)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition duration-100 ease-in bg-white border border-gray-300 rounded-full shadow-sm sm:px-3 sm:py-1 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-200 hover:text-red-800 hover:outline-none">
                        <BsTrashFill className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default OrgRolesForm;
